import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { usePricing } from '../hooks/usePricing';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import WaitlistPopup from '../components/WaitlistPopup';

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [scrollY, setScrollY] = useState(0);
  const [isDark, setIsDark] = useState(false);
  const { pricing } = usePricing();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setNewsletterLoading(true);
    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail, source: 'homepage_footer' })
      });

      const data = await response.json();

      if (response.ok) {
        setNewsletterSubscribed(true);
        setNewsletterEmail('');
        toast.success(data.alreadySubscribed ? 'You\'re already subscribed!' : 'Thanks for subscribing!');
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const features = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      title: 'P2P Video Calls',
      description: 'True peer-to-peer connections with end-to-end encryption',
      badge: 'Encrypted'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
        </svg>
      ),
      title: 'Dynamic Layout',
      description: 'Zoom-like grid that adapts to participant count',
      badge: 'Smart'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Screen Sharing',
      description: 'Share your screen with crystal-clear quality',
      badge: 'HD'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      title: 'P2P Chat',
      description: 'Private peer-to-peer messaging with real-time delivery',
      badge: 'Private'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      title: 'Meeting Notes',
      description: 'Collaborative note-taking with auto-save',
      badge: 'Collaborative'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: 'AI Assistant',
      description: 'Smart transcription and meeting summaries',
      badge: 'Pro'
    }
  ];

  const tiers = pricing ? [
    {
      name: pricing.free.name,
      price: pricing.free.price,
      period: pricing.free.period,
      description: 'Perfect for personal use',
      features: [
        ...pricing.free.features,
        'End-to-end encryption'
      ],
      cta: 'Start Free',
      highlighted: false
    },
    {
      name: pricing.pro.name,
      price: pricing.pro.price,
      period: pricing.pro.period,
      description: 'Best for professionals',
      features: [
        ...pricing.pro.features,
        'Priority support'
      ],
      cta: 'Go Pro',
      highlighted: false
    },
    {
      name: pricing.team.name,
      price: pricing.team.price,
      period: pricing.team.period,
      description: pricing.team.description || 'For small businesses',
      features: [
        'Everything in Pro',
        ...pricing.team.features,
        'Priority support & onboarding'
      ],
      cta: 'Start Team Trial',
      highlighted: true
    }
  ] : [];

  return (
    <div className="min-h-screen bg-white dark:bg-[#000000] transition-colors duration-500">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');

        * {
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }

        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float 6s ease-in-out 1s infinite;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .dark .glass-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .mesh-gradient {
          background:
            radial-gradient(at 27% 37%, hsla(215, 98%, 61%, 0.3) 0px, transparent 50%),
            radial-gradient(at 97% 21%, hsla(125, 98%, 72%, 0.2) 0px, transparent 50%),
            radial-gradient(at 52% 99%, hsla(354, 98%, 61%, 0.2) 0px, transparent 50%),
            radial-gradient(at 10% 29%, hsla(256, 96%, 67%, 0.2) 0px, transparent 50%),
            radial-gradient(at 97% 96%, hsla(38, 60%, 74%, 0.2) 0px, transparent 50%),
            radial-gradient(at 33% 50%, hsla(222, 67%, 73%, 0.2) 0px, transparent 50%),
            radial-gradient(at 79% 53%, hsla(343, 68%, 79%, 0.2) 0px, transparent 50%);
        }

        .grain {
          position: relative;
        }

        .grain::before {
          content: '';
          position: absolute;
          inset: 0;
          opacity: 0.03;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          pointer-events: none;
        }
      `}</style>

      {/* iOS Waitlist Banner - Only show when not logged in */}
      {!user && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#06c] to-[#5856d6] text-white">
          <div className="max-w-[1400px] mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="font-medium">QuickMeet for iOS is coming soon!</span>
            </div>
            <Link
              to="/waitlist"
              className="px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 font-medium transition-colors"
            >
              Join Waitlist
            </Link>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`fixed ${user ? 'top-0' : 'top-[42px]'} w-full z-50 transition-all duration-700 ${
        scrollY > 20
          ? 'bg-white/70 dark:bg-black/70 backdrop-blur-2xl border-b border-black/5 dark:border-white/5'
          : 'bg-transparent'
      }`}>
        <div className="max-w-[1400px] mx-auto px-8 py-5">
          <div className="flex items-center justify-between relative">
            {/* Logo */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2.5 group z-10"
            >
              <div className="relative w-8 h-8">
                <img
                  src={isDark ? "/logo-white.png" : "/logo-blue.png"}
                  alt="QuickMeet"
                  className="w-8 h-8 object-contain"
                />
              </div>
              <span className="text-[21px] font-semibold text-gray-900 dark:text-white tracking-tight">
                QuickMeet
              </span>
            </button>

            {/* Center Navigation - Absolutely positioned */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <button
                onClick={() => navigate('/about')}
                className="text-[14px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                About
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="text-[14px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/security')}
                className="text-[14px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Security
              </button>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-5 z-10">
              <button
                onClick={() => setIsDark(!isDark)}
                className="hidden sm:flex p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <svg className="w-[18px] h-[18px] text-gray-600 dark:text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-[18px] h-[18px] text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>

              <button
                onClick={() => navigate('/login')}
                className="hidden sm:block text-[14px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Sign in
              </button>

              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2 bg-[#06c] dark:bg-white text-white dark:text-black text-[14px] font-medium rounded-full hover:bg-[#0055d4] dark:hover:bg-gray-100 transition-all duration-200"
              >
                Get started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden grain">
        {/* Animated background */}
        <div className="absolute inset-0 mesh-gradient animate-gradient opacity-40 dark:opacity-20"></div>

        {/* Floating orbs */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-[#06c] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-20 animate-float"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-20 animate-float-delayed"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-20 animate-float"></div>

        <div className={`relative max-w-[1400px] mx-auto px-8 ${user ? 'pt-32' : 'pt-40'} pb-32 text-center`}>
          <div className="mb-6 inline-block">
            <span className="px-4 py-2 rounded-full glass-card text-sm font-semibold text-gray-700 dark:text-gray-200 border border-[#06c]/20">
              Free forever. No credit card required.
            </span>
          </div>

          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black text-gray-900 dark:text-white mb-8 leading-none tracking-tight">
            Meet faster.
            <br />
            <span className="bg-gradient-to-r from-[#06c] via-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient">
              Talk smarter.
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto font-light leading-relaxed">
            One app. Every conversation.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-20">
            <button
              onClick={() => navigate('/login')}
              className="group relative px-8 py-4 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-2xl text-lg font-bold hover:shadow-2xl hover:shadow-blue-500/50 hover:scale-105 transition-all duration-300 overflow-hidden"
            >
              <span className="relative z-10">Start Meeting Now</span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0055d4] to-[#06c] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>

            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-4 glass-card text-gray-900 dark:text-white rounded-2xl text-lg font-bold hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              View Pricing
            </button>
          </div>

          {/* Video Preview Card */}
          <div className="relative max-w-5xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-[#06c] to-purple-600 rounded-3xl blur-2xl opacity-20"></div>
            <div className="relative glass-card rounded-3xl p-2 shadow-2xl">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden">
                {/* Mock Video Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 aspect-video">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="relative glass-card rounded-xl overflow-hidden group hover:scale-105 transition-transform duration-300"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20"></div>
                      <div className="relative h-full flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                      {/* Name badge */}
                      <div className="absolute bottom-2 left-2 px-2 py-1 glass-card rounded text-xs font-semibold text-white">
                        User {i + 1}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Controls Bar */}
                <div className="px-4 py-3 border-t border-white/10 flex items-center justify-center gap-3">
                  <div className="w-10 h-10 glass-card rounded-full flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <div className="w-10 h-10 glass-card rounded-full flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div className="w-10 h-10 glass-card rounded-full flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div className="w-10 h-10 glass-card rounded-full flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 px-4 py-2 bg-green-500 text-white rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                End-to-End Encrypted
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Showcase */}
      <section className="relative py-32 px-8 bg-white dark:bg-[#000000] overflow-hidden">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-32">
            <h2 className="text-5xl md:text-7xl font-black text-gray-900 dark:text-white mb-6">
              Everything you need.
              <br />
              <span className="bg-gradient-to-r from-[#06c] via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Nothing you don't.
              </span>
            </h2>
          </div>

          {/* Feature Showcase - Alternating Layout */}
          <div className="space-y-48">
            {/* P2P Video */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="order-2 md:order-1">
                <div className="inline-block px-4 py-1.5 rounded-full bg-[#06c]/10 border border-[#06c]/20 text-[#06c] dark:text-[#0a84ff] text-sm font-bold mb-6">
                  Encrypted
                </div>
                <h3 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-6">
                  True peer-to-peer video
                </h3>
                <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
                  Your video connects directly to participants. No servers in between, no middlemen watching. Just you and them.
                </p>
                <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="font-semibold">End-to-end encrypted by default</span>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="relative aspect-video glass-card rounded-3xl overflow-hidden shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#06c]/20 to-purple-600/20"></div>
                  <div className="grid grid-cols-2 gap-3 p-6 h-full">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="glass-card rounded-2xl flex items-center justify-center relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10"></div>
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Layout */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <div className="relative aspect-video glass-card rounded-3xl overflow-hidden shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20"></div>
                  <div className="grid grid-cols-3 gap-2 p-4 h-full">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="glass-card rounded-xl"></div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div className="inline-block px-4 py-1.5 rounded-full bg-purple-600/10 border border-purple-600/20 text-purple-600 dark:text-purple-400 text-sm font-bold mb-6">
                  Smart
                </div>
                <h3 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-6">
                  Layout that adapts
                </h3>
                <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Grid automatically adjusts to the number of participants. From 1-on-1 to large meetings, everyone gets equal space.
                </p>
              </div>
            </div>

            {/* Screen Sharing */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="order-2 md:order-1">
                <div className="inline-block px-4 py-1.5 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-600 dark:text-blue-400 text-sm font-bold mb-6">
                  Share
                </div>
                <h3 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-6">
                  Screen sharing
                </h3>
                <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Share your screen with participants. 720p on free, 1080p with Pro. Perfect for presentations and demos.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <div className="relative aspect-video glass-card rounded-3xl overflow-hidden shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20"></div>
                  <div className="p-6 h-full flex items-center justify-center">
                    <svg className="w-24 h-24 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <div className="relative aspect-video glass-card rounded-3xl overflow-hidden shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 to-purple-500/20"></div>
                  <div className="p-6 h-full flex flex-col justify-between">
                    <div className="space-y-3">
                      <div className="glass-card rounded-xl p-3 ml-auto max-w-[80%]">
                        <div className="h-2 bg-gray-400/30 rounded w-32"></div>
                      </div>
                      <div className="glass-card rounded-xl p-3 mr-auto max-w-[80%]">
                        <div className="h-2 bg-gray-400/30 rounded w-24"></div>
                      </div>
                      <div className="glass-card rounded-xl p-3 ml-auto max-w-[80%]">
                        <div className="h-2 bg-gray-400/30 rounded w-28"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="inline-block px-4 py-1.5 rounded-full bg-pink-600/10 border border-pink-600/20 text-pink-600 dark:text-pink-400 text-sm font-bold mb-6">
                  Private
                </div>
                <h3 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-6">
                  P2P encrypted chat
                </h3>
                <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  Send messages directly to participants with end-to-end encryption. Your conversations stay private.
                </p>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <svg className="w-5 h-5 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Real-time delivery</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <svg className="w-5 h-5 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save chat history (optional)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats section */}
          <div className="mt-48 grid grid-cols-2 md:grid-cols-4 gap-12">
            <div className="text-center">
              <div className="text-5xl md:text-6xl font-black bg-gradient-to-r from-[#06c] to-purple-600 bg-clip-text text-transparent mb-3">
                720p
              </div>
              <p className="text-gray-600 dark:text-gray-400 font-medium">Free tier quality</p>
            </div>
            <div className="text-center">
              <div className="text-5xl md:text-6xl font-black bg-gradient-to-r from-[#06c] to-purple-600 bg-clip-text text-transparent mb-3">
                1080p
              </div>
              <p className="text-gray-600 dark:text-gray-400 font-medium">Pro quality</p>
            </div>
            <div className="text-center">
              <div className="text-5xl md:text-6xl font-black bg-gradient-to-r from-[#06c] to-purple-600 bg-clip-text text-transparent mb-3">
                4-∞
              </div>
              <p className="text-gray-600 dark:text-gray-400 font-medium">Participants</p>
            </div>
            <div className="text-center">
              <div className="text-5xl md:text-6xl font-black bg-gradient-to-r from-[#06c] to-purple-600 bg-clip-text text-transparent mb-3">
                ∞
              </div>
              <p className="text-gray-600 dark:text-gray-400 font-medium">Time limit</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative py-32 px-8 bg-white dark:bg-[#000000] grain overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#06c] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-10 animate-float"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-10 animate-float-delayed"></div>

        <div className="relative max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-7xl font-black text-gray-900 dark:text-white mb-6">
              Choose your plan
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Start free, upgrade when you need more
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tier, index) => (
              <div
                key={index}
                className={`relative rounded-3xl p-8 transition-all duration-500 hover:scale-105 ${
                  tier.highlighted
                    ? 'bg-gradient-to-br from-[#06c] to-[#0055d4] text-white shadow-2xl shadow-blue-500/50 scale-105 md:scale-110'
                    : 'glass-card text-gray-900 dark:text-white'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-white text-[#06c] rounded-full text-sm font-bold whitespace-nowrap">
                    Most Popular
                  </div>
                )}

                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                  <p className={`text-sm mb-6 ${tier.highlighted ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'}`}>
                    {tier.description}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black">{tier.price}</span>
                    <span className={`text-sm ${tier.highlighted ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                      {tier.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg
                        className={`w-6 h-6 flex-shrink-0 mt-0.5 ${
                          tier.highlighted ? 'text-white' : 'text-[#06c]'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={`${tier.highlighted ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/login')}
                  className={`w-full py-4 rounded-2xl font-bold text-lg transition-all duration-300 ${
                    tier.highlighted
                      ? 'bg-white text-[#06c] hover:shadow-xl hover:bg-gray-50'
                      : 'bg-gradient-to-r from-[#06c] to-[#0055d4] text-white hover:shadow-lg hover:shadow-blue-500/30'
                  }`}
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="mt-20 flex flex-wrap items-center justify-center gap-8 text-center">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">No credit card required</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">Secure payments</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 px-8 bg-gradient-to-br from-[#06c] to-[#0055d4] overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white to-transparent animate-gradient"></div>
        </div>

        <div className="relative max-w-[1080px] mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8">
            Ready to connect?
          </h2>
          <p className="text-xl md:text-2xl text-white/90 mb-12 font-medium">
            Join thousands using QuickMeet for their daily video calls
          </p>

          <button
            onClick={() => navigate('/login')}
            className="group relative px-12 py-5 bg-white text-[#06c] rounded-2xl text-xl font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300 overflow-hidden"
          >
            <span className="relative z-10">Start Your First Meeting</span>
            <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>

          <p className="mt-6 text-white/70 text-sm">
            No download required • Start in 10 seconds • Free forever
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-black text-gray-400 py-16 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <img
                  src="/logo-white.png"
                  alt="QuickMeet"
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <h3 className="text-xl font-bold text-white">QuickMeet</h3>
                  <span className="text-sm">by TheJhyeFactor</span>
                </div>
              </div>
              <p className="text-gray-400 max-w-md">
                Professional video conferencing made simple. Connect with anyone, anywhere, for free.
              </p>
            </div>

            {/* Newsletter Signup */}
            <div>
              <h4 className="text-white font-bold mb-4">Newsletter</h4>
              <p className="text-gray-400 text-sm mb-4">
                Get product updates and tips delivered to your inbox.
              </p>
              {newsletterSubscribed ? (
                <div className="flex items-center gap-2 text-green-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Thanks for subscribing!</span>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#06c] focus:border-transparent text-sm"
                  />
                  <button
                    type="submit"
                    disabled={newsletterLoading || !newsletterEmail}
                    className="w-full py-2.5 bg-[#06c] text-white rounded-xl font-medium text-sm hover:bg-[#0055d4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              )}
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><button onClick={() => navigate('/pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => navigate('/security')} className="hover:text-white transition-colors">Security</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About</button></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">© 2025 TheJhyeFactor. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="https://twitter.com/TheJhyeFactor" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/in/jhye-o-meley-529960213/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Waitlist Popup - appears on scroll */}
      <WaitlistPopup />
    </div>
  );
}
